
import React, { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DebugConsole } from "@/components/admin/settings/DebugConsole";
import { EmailVerificationTab } from "@/components/admin/settings/EmailVerificationTab";
import { VerificationManagerTab } from "@/components/admin/settings/VerificationManagerTab";
import { useAdminSettings } from "@/hooks/useAdminSettings";
import { RawSettingsTab } from "@/components/admin/settings/RawSettingsTab";
import { TestingModeTab } from "@/components/admin/settings/TestingModeTab";
import { DebugReviewsTab } from "@/components/admin/settings/DebugReviewsTab";
import { DiagnosticsTab } from "@/components/admin/settings/DiagnosticsTab";
import { EditorModeTab } from "@/components/admin/settings/EditorModeTab";
import { DebugDocTab } from "@/components/admin/settings/DebugDocTab";
import { MpesaTab } from "@/components/admin/settings/MpesaTab";
import { SmsTab } from "@/components/admin/settings/SmsTab";
import { SmsNotificationsTab } from "@/components/admin/settings/SmsNotificationsTab";
import { Bug, Activity, FileText, Edit, BookOpen, CreditCard, MessageSquare, Bell } from "lucide-react";

const AdminTesting = () => {
  const { settings, isSaving, isLoading, saveSettings, updateSettings } = useAdminSettings();
  const [activeTab, setActiveTab] = useState("testing");

  if (isLoading) {
    return (
      <AdminLayout title="Testing">
        <div className="flex justify-center items-center h-64">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    );
  }
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <AdminLayout title="Testing">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Testing & Debugging</h1>
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="space-y-2 mb-4">
          {/* First row - Core Testing & Debug Tools */}
          <TabsList className="w-full justify-start">
            <TabsTrigger value="testing">Testing Mode</TabsTrigger>
            <TabsTrigger value="diagnostics" className="text-red-600">
              <div className="flex items-center gap-1">
                <Activity className="h-4 w-4" />
                <span>Diagnostics</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="debug-console">
              <div className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                <span>App Log</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="reviews-debug">
              <div className="flex items-center gap-1">
                <Bug className="h-4 w-4" />
                <span>Reviews Debug</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="debug-doc">
              <div className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                <span>DEBUG.md</span>
              </div>
            </TabsTrigger>
          </TabsList>
          
          {/* Second row - Configuration & Integration Tools */}
          <TabsList className="w-full justify-start">
            <TabsTrigger value="mpesa">
              <div className="flex items-center gap-1">
                <CreditCard className="h-4 w-4" />
                <span>MPesa</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="sms">
              <div className="flex items-center gap-1">
                <MessageSquare className="h-4 w-4" />
                <span>SMS</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="sms-notifications">
              <div className="flex items-center gap-1">
                <Bell className="h-4 w-4" />
                <span>SMS Notifications</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="editor-mode">
              <div className="flex items-center gap-1">
                <Edit className="h-4 w-4" />
                <span>Editor Mode</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="email-verification">Email Verification</TabsTrigger>
            <TabsTrigger value="verification-manager">Data Management</TabsTrigger>
            <TabsTrigger value="raw-settings">Raw Settings</TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="testing">
          <TestingModeTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="mpesa">
          <MpesaTab />
        </TabsContent>
        
        <TabsContent value="sms">
          <SmsTab />
        </TabsContent>
        
        <TabsContent value="sms-notifications">
          <SmsNotificationsTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="raw-settings">
          <RawSettingsTab settings={settings} />
        </TabsContent>
        
        <TabsContent value="debug-console">
          <DebugConsole />
        </TabsContent>
        
        <TabsContent value="diagnostics">
          <DiagnosticsTab />
        </TabsContent>
        
        <TabsContent value="editor-mode">
          <EditorModeTab />
        </TabsContent>
        
        <TabsContent value="debug-doc">
          <DebugDocTab />
        </TabsContent>
        
        <TabsContent value="reviews-debug">
          <DebugReviewsTab />
        </TabsContent>
        
        <TabsContent value="email-verification">
          <EmailVerificationTab />
        </TabsContent>
        
        <TabsContent value="verification-manager">
          <VerificationManagerTab />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminTesting;
